<template>
  <Head>
    <meta
      v-if="published"
      name="robots"
      content="all"
    >
    <meta
      v-else
      name="robots"
      content="none"
    >

    <meta
      property="og:title"
      :content="title"
    >
    <meta
      property="og:type"
      content="website"
    >
    <meta
      property="og:image"
      :content="coverImageUrl"
    >
  </Head>
  <div class="checkout-screen">
    <div
      class="flex flex-col overflow-x-hidden flex-grow pb-32 xl:pb-0 cart-clearance"
      :class="{ 'overflow-hidden': cartInteracting }"
    >
      <CheckoutHeader current-screen="tickets" />
      <section
        class="checkout-main"
        :aria-label="$gettext('Tickets')"
      >
        <EventDetails
          v-if="event"
          :event="event"
          :hide-tickets="hideTickets"
        />
        <Transition>
          <AccessCodeInput
            v-if="showAccessCodeInput"
            :key="`aci-${aciCounter}`"
            :title="$gettext('Access code')"
            :instructions="$gettext('Have an access code? Enter it here.')"
            :placeholder="$gettext('Access code')"
            :accessible-label="$gettext('Add access code')"
            @apply="onApplyAccessCode"
          />
        </Transition>
        <Transition>
          <AccessCodeInput
            v-if="checkoutStore.presalePasswordRequired"
            key="presale-password-input"
            :title="$gettext('Presale Password')"
            :instructions="$gettext('This presale requires a password.')"
            :placeholder="$gettext('Password')"
            :accessible-label="$gettext('Enter presale password')"
            variant="password"
            @apply="onApplyAccessCode"
          />
        </Transition>
        <div
          v-if="event?.footerContent"
          class="footer"
        >
          <MinimumAgeDisplay
            :minimum-age="minimumAge"
            variant="footer"
          />
          <div class="flex items-start gap-2">
            <iconify-icon
              v-if="event?.footerContent"
              icon="mdi:information-outline"
              class="text-lg font-light text-vp-base-600"
            />
            <div
              class="footer-content"
              v-html="sanitizeHtml(event.footerContent)"
            />
          </div>
        </div>
      </section>
    </div>
    <section
      class="px-3 lg:px-0 lg:sticky lg:top-0"
      :aria-label="$gettext('Cart')"
    >
      <div class="cart shopping-cart px-3 overflow-x-auto lg:overflow-x-hidden">
        <ShoppingCart
          v-if="event && shoppingCart"
          show-reset
          :event="event"
          :items="shoppingCartItems"
          :price-breakdown="priceBreakdown"
          :control-quantity="shoppingCart.controlQuantity"
          class="min-w-full"
        >
          <template #footer="{ isExpanded }">
            <div v-if="checkoutStore.hasValidLock && (priceBreakdown.orderTotal ?? 0) > 0">
              <StripePaymentForm
                hide-form
                hide-submit
                :amount="priceBreakdown.orderTotal"
                :hide-checkboxes="['terms_of_service']"
                :quick-checkout-tos="event.config?.quickCheckoutTos"
                @quick-checkout-load="onQuickCheckoutLoad"
              >
                <template #footer>
                  <div class="my-2 sm:my-4 flex justify-center items-center gap-4 text-vp-base-400">
                    <hr class="grow border">
                    <span class="uppercase">{{ $gettext('or') }}</span>
                    <hr class="grow border">
                  </div>
                </template>
              </StripePaymentForm>
            </div>

            <ShoppingCartFooter
              show-next
              :next-label="nextLabel"
              :class="isExpanded ? '' : 'hidden xl:block'"
              @next="onNext"
            />
          </template>
        </ShoppingCart>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import {
  TicketLockingError,
  useCheckoutStore,
} from './stores/checkout';
import { computed, onMounted, onServerPrefetch, ref } from 'vue';
import EventDetails from './EventDetails.vue';
import ShoppingCart from './ShoppingCart.vue';
import { Checkout } from '@vp/common';
import { useGettext } from 'vue3-gettext';
import { useToast } from 'vue-toastification';
import ShoppingCartFooter from './ShoppingCartFooter.vue';
import { useRouter } from 'vue-router';
import { navigate } from 'vike/client/router';
import { useCheckoutTitle } from './useCheckoutTitle';
import { useConfig } from 'vike-vue/useConfig';
import { render } from 'vike/abort';
import { Head } from 'vike-vue/Head';
import { usePageContext } from 'vike-vue/usePageContext';
import { initEventId } from './utils/initEventId';
import { NotFoundError } from '@vp/common/errors';
import sanitizeHtml from 'sanitize-html';
import { clientOnly } from 'vike-vue/clientOnly';
import MinimumAgeDisplay from './MinimumAgeDisplay.vue';
import { useEventImage } from './composables/useEventImage';
import CheckoutHeader from './CheckoutHeader.vue';
import * as VPErrorLegacy from './types/VPError';
import { useTracking } from './components/tracking/useTracking';
import AccessCodeInput from './AccessCodeInput.vue';
import { useSelectedEvent } from './composables/useSelectedEvent';
import { wasVerificationCancelled } from './utils/verificationCancelled';
import * as Sentry from '@sentry/vue';

// StripePaymentForm pulls in browser-only integrations (Stripe, Hive) and calls
// useHive() in setup, which throws during SSR. Load it client-only so the cart's
// "ready to pay" state (valid lock + positive total) can server-render without it.
const StripePaymentForm = clientOnly(() => import('./StripePaymentForm.vue'));

const checkoutStore = useCheckoutStore();
const { $gettext } = useGettext();
const toast = useToast();
const router = useRouter();
const { title } = useCheckoutTitle();
const pageContext = usePageContext();
const { trackEvent } = useTracking();

const cartInteracting = ref(false);
const quickCheckoutAvailable = ref(false);

const aciCounter = ref(0);

const onQuickCheckoutLoad = () => quickCheckoutAvailable.value = true;

onServerPrefetch(async () => {
  const config = useConfig();
  try {
    await checkoutStore.init();
    config({
      title: title.value,
    });
  } catch (e) {
    if (e instanceof NotFoundError) {
      throw render(404, $gettext('Something went wrong, please try again later.'));
    }

    console.error('caught in prefetch', e);
    throw e;
  }
});

onMounted(async () => {
  // TODO: Needs to be set differently
  initEventId(pageContext);
  await checkoutStore.init();

  if (pageContext.urlParsed.search.presaleName) {
    applyAccessCode(pageContext.urlParsed.search.presaleName);
  }
});

const { event } = useSelectedEvent();

const coverImageUrl = useEventImage(event);

const shoppingCart = computed(() => checkoutStore.shoppingCart);
const shoppingCartItems = computed(() => checkoutStore.getCartedTickets.filter(({ count }) => (count ?? 0) > 0));
const priceBreakdown = computed<Checkout.PriceBreakdown.PriceBreakdown>(() => shoppingCart.value?.priceBreakdown ?? Checkout.PriceBreakdown.getNew());
const published = computed(() => checkoutStore.getEventCheckoutConfig?.published);

const presaleParam = ref<string | null | undefined>(pageContext.urlParsed.search.presaleName);
const showAccessCodeInput = computed(() => !checkoutStore.presalePasswordRequired && checkoutStore.getEventCheckoutConfig?.accessCode);
const hideTickets = computed(() => Boolean(checkoutStore.presalePasswordRequired || presaleParam.value));

const minimumAge = computed(() => event.value?.minimumAge);

const nextLabel = computed(() => (priceBreakdown.value?.orderTotal || 0) && quickCheckoutAvailable.value ? $gettext('Pay another way') : $gettext('Checkout'));

const applyAccessCode = async (code: string) => {
  const presale = Checkout.ShoppingCartItemInput.newPresale(code);
  try {
    await checkoutStore.storeShoppingCartItem(presale);
    trackEvent('access_code_applied');
    toast.success($gettext('Access code applied'));
    aciCounter.value++;
    // Before `presaleParam` is cleared below, and it must stay that way: this sets
    // `ticketsLoading` to LOADING synchronously, so EventDetails' `view_item` — which is
    // gated on `hideTickets` — waits and reports the revealed tiers rather than the public
    // catalogue. See packages/checkout/doc/claude/analytics.md.
    checkoutStore.fetchAvailableTickets({ force: true });
    checkoutStore.presalePasswordRequired = false;

    if (typeof window !== 'undefined') {
      const targetUrl = new URL(window.location.href);
      targetUrl.searchParams.delete('presaleName');
      window.history.replaceState(null, '', targetUrl.href);
      presaleParam.value = null;
    }
  } catch (e) {
    if (wasVerificationCancelled(e)) {
      toast.info($gettext('Verification cancelled'));
      return;
    }

    if ((e as VPErrorLegacy.VPError).type == 'VPError') {
      const vpError = e as VPErrorLegacy.VPError;
      for (const msg of vpError.errors) {
        toast.error(msg);
      }
    } else if (e instanceof Checkout.ShoppingCartError && e.code === 'PASSWORD_REQUIRED') {
      checkoutStore.presalePasswordRequired = true;
      toast.info($gettext('This presale requires a password'));
      if (typeof window !== 'undefined') {
        const targetUrl = new URL(window.location.href);
        targetUrl.searchParams.delete('presaleName');
        window.history.replaceState(null, '', targetUrl.href);
      }
    } else {
      // Neither caller awaits applyAccessCode, so rethrowing here only produced an
      // unhandled rejection. Report it directly, as onNext does.
      toast.error($gettext('Error applying access code'));
      Sentry.captureException(e, { extra: { context: 'Error applying access code' } });
    }
  }
};

const onApplyAccessCode = (_code: string) => {
  const code = _code.trim();
  if (!code || code == '') {
    return;
  }

  applyAccessCode(code);
};

const onNext = async () => {
  try {
    await checkoutStore.acquireLock();

    const slug = event.value?.slug;
    if (!slug) {
      throw new Error('No event selected');
    }

    await navigate(router.resolve({ name: 'OrderPayment', params: { eventId: slug } }).href);
  } catch (e) {
    if (e instanceof TicketLockingError) {
      alert(e.message || $gettext('Some of the tickets you requested could not be reserved!'));
      return;
    }

    if (wasVerificationCancelled(e)) {
      toast.info($gettext('Verification cancelled'));
      return;
    }

    console.error('acquire lock error:', e);
    toast.error($gettext('Something went wrong, please try again later.'));
    Sentry.captureException(e, { extra: { context: 'Error acquiring shopping cart lock' } });
  }
};
</script>
